import { io, Socket } from "socket.io-client";
import {
  IChatConfig,
  IChatSession,
  IChatSessionStatus,
  IMessage,
  IStatus,
  SocketEvent,
} from "atlas-shared";

export interface ChatSessionState {
  messages: IMessage[];
  sessionStatus: IChatSessionStatus | null;
  chatConfig: IChatConfig | null;
  chatSession: IChatSession | null;
  isTyping: boolean;
  userIsTyping: boolean;
  loadedMessages: boolean;
  loadingMessages: boolean;
  joinedAgents: Map<string, Set<number>>; // Track which agents are joined to each session (sessionId -> Set<userId>)
  pendingAgents: Map<string, Set<number>>; // Track pending agents while connecting
}

export interface ChatSessionSubscriber {
  onMessagesUpdate: (messages: IMessage[]) => void;
  onSessionStatusUpdate: (status: IChatSessionStatus | null) => void;
  onLoadingUpdate: (loading: boolean) => void;
  onUserTypingUpdate: (isTyping: boolean) => void;
}

/**
 * Singleton class that manages chat session socket connections and state.
 * This prevents multiple socket connections and ensures consistent state management
 * across all components that use chat sessions.
 */
export class ChatSessionManager {
  private static instance: ChatSessionManager;

  private sockets: Record<number, Socket> = {};

  private socketUsageCount: Record<number, number> = {};

  private sessionStates: Record<number, ChatSessionState> = {};

  private subscribers: Record<number, Set<ChatSessionSubscriber>> = {};

  private typingTimeouts: Record<number, NodeJS.Timeout> = {};

  private connectionStates: Record<number, "disconnected" | "connecting" | "connected"> = {};

  private constructor() {}

  /**
   * Get the singleton instance of ChatSessionManager
   */
  public static getInstance(): ChatSessionManager {
    if (!ChatSessionManager.instance) {
      ChatSessionManager.instance = new ChatSessionManager();
    }
    return ChatSessionManager.instance;
  }

  /**
   * Subscribe to chat session updates for a specific conversation.
   * @param conversationId - The conversation ID to subscribe to
   * @param subscriber - Callback functions for handling updates
   * @returns Unsubscribe function
   */
  public subscribe(conversationId: number, subscriber: ChatSessionSubscriber): () => void {
    if (!this.subscribers[conversationId]) {
      this.subscribers[conversationId] = new Set();
    }
    this.subscribers[conversationId].add(subscriber);

    // Initialize state if it doesn't exist
    if (!this.sessionStates[conversationId]) {
      this.initializeSessionState(conversationId);
    }

    // Immediately notify subscriber of current state
    const state = this.sessionStates[conversationId];
    subscriber.onMessagesUpdate(state.messages);
    subscriber.onSessionStatusUpdate(state.sessionStatus);
    subscriber.onLoadingUpdate(state.loadingMessages);
    subscriber.onUserTypingUpdate(state.userIsTyping);

    // Return unsubscribe function
    return () => {
      this.subscribers[conversationId]?.delete(subscriber);
      if (this.subscribers[conversationId]?.size === 0) {
        delete this.subscribers[conversationId];
        this.disconnectSocket(conversationId);
      }
    };
  }

  private initializeSessionState(conversationId: number): void {
    this.sessionStates[conversationId] = {
      messages: [],
      sessionStatus: null,
      chatConfig: null,
      chatSession: null,
      isTyping: false,
      userIsTyping: false,
      loadedMessages: false,
      loadingMessages: false,
      joinedAgents: new Map<string, Set<number>>(),
      pendingAgents: new Map<string, Set<number>>(),
    };
  }

  private notifySubscribers(conversationId: number): void {
    const state = this.sessionStates[conversationId];
    const subscribers = this.subscribers[conversationId];

    if (!state || !subscribers) return;

    subscribers.forEach((subscriber) => {
      subscriber.onMessagesUpdate(state.messages);
      subscriber.onSessionStatusUpdate(state.sessionStatus);
      subscriber.onLoadingUpdate(state.loadingMessages);
      subscriber.onUserTypingUpdate(state.userIsTyping);
    });
  }

  public getOrCreateSocket(conversationId: number): Socket {
    const url = process.env.REACT_APP_BASE_URL as string;

    if (!this.sockets[conversationId]) {
      this.sockets[conversationId] = io(url, { path: "/chat" });
      this.socketUsageCount[conversationId] = 0;
      this.connectionStates[conversationId] = "disconnected";
      this.setupSocketEventHandlers(conversationId);
    }
    this.socketUsageCount[conversationId]++;

    return this.sockets[conversationId];
  }

  private setupSocketEventHandlers(conversationId: number): void {
    const socket = this.sockets[conversationId];
    if (!socket) return;

    socket.on("disconnect", () => {
      this.connectionStates[conversationId] = "disconnected";
      this.handleSocketDisconnection(conversationId);
    });

    socket.on(SocketEvent.CHAT_IN_MESSAGE, (data: IMessage) => {
      this.addMessage(conversationId, data);
    });

    socket.on(SocketEvent.CHAT_OUT_MESSAGE, (data: IMessage) => {
      this.addMessage(conversationId, data);
    });

    socket.on(SocketEvent.CHAT_USER_STATUS, (data) => {
      const status: IStatus = data.status;
      this.handleUserTypingStatus(conversationId, status);
    });

    socket.on(SocketEvent.CHAT_SESSION_TIMED_OUT, (data: IMessage) => {
      this.addMessage(conversationId, data);
      this.updateSessionStatus(conversationId, IChatSessionStatus.TIMED_OUT);
    });

    socket.on(SocketEvent.CHAT_SESSION_ENDED, (data: IMessage) => {
      this.addMessage(conversationId, data);
      this.updateSessionStatus(conversationId, IChatSessionStatus.ENDED);
      // Clear joined agents and pending agents for the ended session
      const state = this.sessionStates[conversationId];
      if (state?.chatSession?.sessionId) {
        state.joinedAgents.delete(state.chatSession.sessionId);
        state.pendingAgents.delete(state.chatSession.sessionId);
      }
    });
  }

  private addMessage(conversationId: number, message: IMessage): void {
    const state = this.sessionStates[conversationId];
    if (!state) return;

    state.messages = [message, ...state.messages];
    this.notifySubscribers(conversationId);
  }

  private updateSessionStatus(conversationId: number, status: IChatSessionStatus | null): void {
    const state = this.sessionStates[conversationId];
    if (!state) return;

    state.sessionStatus = status;
    this.notifySubscribers(conversationId);
  }

  private handleSocketDisconnection(conversationId: number): void {
    const state = this.sessionStates[conversationId];
    if (!state) return;

    // Reset connection state
    this.connectionStates[conversationId] = "disconnected";

    state.sessionStatus = null;
    state.chatConfig = null;
    state.chatSession = null;
    state.isTyping = false;
    state.userIsTyping = false;
    // Clear all joined agents and pending agents on disconnect
    state.joinedAgents.clear();
    state.pendingAgents.clear();

    if (this.typingTimeouts[conversationId]) {
      clearTimeout(this.typingTimeouts[conversationId]);
      delete this.typingTimeouts[conversationId];
    }

    this.notifySubscribers(conversationId);
  }

  private handleUserTypingStatus(conversationId: number, status: IStatus): void {
    const state = this.sessionStates[conversationId];
    if (!state) return;

    const isTyping = status === IStatus.TYPING;

    if (state.userIsTyping !== isTyping) {
      state.userIsTyping = isTyping;
      this.notifySubscribers(conversationId);
    }
  }

  public connectChatSession(
    conversationId: number,
    chatConfigId: number,
    sessionId: string,
    userId: number
  ): void {
    const socket = this.getOrCreateSocket(conversationId);
    const state = this.sessionStates[conversationId];
    const connectionState = this.connectionStates[conversationId];

    if (
      !state ||
      state.sessionStatus === IChatSessionStatus.ENDED ||
      state.sessionStatus === IChatSessionStatus.TIMED_OUT
    ) {
      return;
    }

    const chatConfig = { id: chatConfigId } as IChatConfig;
    const chatSession = { sessionId: sessionId } as IChatSession;

    // Update the current session info
    state.chatConfig = chatConfig;
    state.chatSession = chatSession;

    // Check if this agent is already joined or pending for this specific session
    const sessionAgents = state.joinedAgents.get(sessionId) || new Set<number>();
    const pendingAgents = state.pendingAgents.get(sessionId) || new Set<number>();
    const isAlreadyJoined = sessionAgents.has(userId);
    const isPending = pendingAgents.has(userId);

    if (isAlreadyJoined) {
      this.updateSessionStatus(conversationId, IChatSessionStatus.ACTIVE);
      return;
    }

    if (isPending) {
      return;
    }

    const handleConnect = () => {
      if (
        state.sessionStatus === IChatSessionStatus.ENDED ||
        state.sessionStatus === IChatSessionStatus.TIMED_OUT
      ) {
        return;
      }

      // Process all pending agents for this session
      const currentPendingAgents = state.pendingAgents.get(sessionId) || new Set<number>();
      const currentJoinedAgents = state.joinedAgents.get(sessionId) || new Set<number>();

      currentPendingAgents.forEach((pendingUserId) => {
        if (!currentJoinedAgents.has(pendingUserId)) {
          socket.emit(SocketEvent.CHAT_AGENT_JOINED, {
            chatConfig,
            chatSession,
            user: { id: pendingUserId },
          });

          currentJoinedAgents.add(pendingUserId);
        }
      });

      // Clear all pending agents for this session
      currentPendingAgents.clear();
      state.pendingAgents.set(sessionId, currentPendingAgents);
      state.joinedAgents.set(sessionId, currentJoinedAgents);

      this.updateSessionStatus(conversationId, IChatSessionStatus.ACTIVE);
    };

    // Mark as pending
    pendingAgents.add(userId);
    state.pendingAgents.set(sessionId, pendingAgents);

    // If socket is already connected, handle immediately
    if (connectionState === "connected") {
      handleConnect();
    } else if (connectionState === "disconnected") {
      // Mark as connecting to prevent multiple connect handlers for the same session
      this.connectionStates[conversationId] = "connecting";

      // Set up one-time connect handler
      const onConnect = () => {
        socket.off("connect", onConnect); // Remove this specific handler
        this.connectionStates[conversationId] = "connected";
        handleConnect();
      };

      socket.on("connect", onConnect);
    }
  }

  /**
   * Check if a specific agent is joined to the current session of a conversation
   */
  public isAgentJoined(conversationId: number, userId: number): boolean {
    const state = this.sessionStates[conversationId];
    if (!state?.chatSession?.sessionId) return false;

    const sessionAgents = state.joinedAgents.get(state.chatSession.sessionId);
    return sessionAgents ? sessionAgents.has(userId) : false;
  }

  public emitTypingStatus(conversationId: number, status: IStatus.TYPING | IStatus.IDLE): void {
    const socket = this.sockets[conversationId];
    const state = this.sessionStates[conversationId];

    if (!socket || !state?.chatConfig || !state?.chatSession) return;

    socket.emit(SocketEvent.CHAT_AGENT_STATUS, {
      status,
      chatConfig: { id: state.chatConfig.id },
      chatSession: {
        id: state.chatSession.id,
        sessionId: state.chatSession.sessionId,
      },
    });
  }

  public handleTypingStart(conversationId: number): void {
    const state = this.sessionStates[conversationId];
    if (!state) return;

    // Clear any existing timeout
    if (this.typingTimeouts[conversationId]) {
      clearTimeout(this.typingTimeouts[conversationId]);
    }

    // Only emit typing if we weren't already typing
    if (!state.isTyping) {
      state.isTyping = true;
      this.emitTypingStatus(conversationId, IStatus.TYPING);
    }

    // Set a new timeout for 3 seconds
    this.typingTimeouts[conversationId] = setTimeout(() => {
      this.handleTypingStop(conversationId);
    }, 3000);
  }

  public handleTypingStop(conversationId: number): void {
    const state = this.sessionStates[conversationId];
    if (!state) return;

    // Clear the timeout and immediately emit IDLE
    if (this.typingTimeouts[conversationId]) {
      clearTimeout(this.typingTimeouts[conversationId]);
      delete this.typingTimeouts[conversationId];
    }

    if (state.isTyping) {
      state.isTyping = false;
      this.emitTypingStatus(conversationId, IStatus.IDLE);
    }
  }

  public setMessages(conversationId: number, messages: IMessage[]): void {
    const state = this.sessionStates[conversationId];
    if (!state) return;

    state.messages = messages;
    state.loadedMessages = true;
    state.loadingMessages = false;
    this.notifySubscribers(conversationId);
  }

  public setLoadingMessages(conversationId: number, loading: boolean): void {
    const state = this.sessionStates[conversationId];
    if (!state) return;

    state.loadingMessages = loading;
    this.notifySubscribers(conversationId);
  }

  public getSessionState(conversationId: number): ChatSessionState | null {
    return this.sessionStates[conversationId] || null;
  }

  public getSocket(conversationId: number): Socket | null {
    return this.sockets[conversationId] || null;
  }

  /**
   * Check if a session is already joined and active for a conversation
   */
  public isSessionConnectedAndActive(conversationId: number, sessionId?: string): boolean {
    const state = this.sessionStates[conversationId];
    const connectionState = this.connectionStates[conversationId];

    if (!state || connectionState !== "connected") return false;

    // If sessionId is provided, check if it matches the current session
    if (sessionId && state.chatSession?.sessionId !== sessionId) return false;

    return state.sessionStatus === IChatSessionStatus.ACTIVE;
  }

  /**
   * Check if an agent is already joined to any session for this conversation
   */
  public isAgentConnectedToConversation(conversationId: number, userId: number): boolean {
    const state = this.sessionStates[conversationId];
    if (!state || !state.chatSession?.sessionId) return false;

    return this.isAgentJoined(conversationId, userId);
  }

  private disconnectSocket(conversationId: number): void {
    if (!this.sockets[conversationId]) {
      return;
    }

    this.socketUsageCount[conversationId] = Math.max(0, this.socketUsageCount[conversationId] - 1);

    if (this.socketUsageCount[conversationId] === 0) {
      this.sockets[conversationId].disconnect();
      delete this.sockets[conversationId];
      delete this.socketUsageCount[conversationId];
      delete this.connectionStates[conversationId];
      delete this.sessionStates[conversationId];

      if (this.typingTimeouts[conversationId]) {
        clearTimeout(this.typingTimeouts[conversationId]);
        delete this.typingTimeouts[conversationId];
      }
    }
  }

  public forceDisconnect(conversationId: number): void {
    if (this.sockets[conversationId]) {
      this.sockets[conversationId].disconnect();
      delete this.sockets[conversationId];
      delete this.socketUsageCount[conversationId];
      delete this.connectionStates[conversationId];
      delete this.sessionStates[conversationId];

      if (this.typingTimeouts[conversationId]) {
        clearTimeout(this.typingTimeouts[conversationId]);
        delete this.typingTimeouts[conversationId];
      }
    }
  }

  public getAllActiveSockets(): Record<number, Socket> {
    return { ...this.sockets };
  }
}

export default ChatSessionManager.getInstance();
